import React, { useState, useEffect, useCallback, useContext } from "react";
import { Button, Input } from "reactstrap";
import Swal from "sweetalert2";
import Header from "../../layout/Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { posGet, avisaError, numero, hoy, sumaDias, fechaHora, variante } from "./posApi";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import VariantePicker from "./VariantePicker";
import SinPermiso from "./SinPermiso";
import { imprimirEtiquetas } from "./etiquetasPdf";

const FORMATO_KEY = "pos-etiquetas-formato";

function PosEtiquetas() {
  const { user } = useContext(AuthContext);
  const ctx = useTienda(false);
  const { tienda } = ctx;
  const [codigo, setCodigo] = useState("");
  const [picker, setPicker] = useState(null);
  const [lista, setLista] = useState([]); // {k, codigo, nombre, color, talla, precioTienda, codigoBarras, cantidad}
  const [entradas, setEntradas] = useState([]);
  const [formato, setFormato] = useState(() => { try { return localStorage.getItem(FORMATO_KEY) || "rollo"; } catch (e) { return "rollo"; } });
  const [conPrecio, setConPrecio] = useState(true);

  const cargarEntradas = useCallback(() => {
    if (!tienda) return;
    posGet("/entradas", { area: tienda, inicio: sumaDias(hoy(), -30), fin: hoy() }).then((r) => setEntradas(r.filter((e) => e.estatus === "Activa"))).catch(() => setEntradas([]));
  }, [tienda]);
  useEffect(() => { cargarEntradas(); }, [cargarEntradas]);

  const agrega = (filas) => setLista((prev) => {
    const out = [...prev];
    filas.forEach((f) => {
      const i = out.findIndex((x) => x.codigoBarras === f.codigoBarras);
      if (i >= 0) out[i] = { ...out[i], cantidad: Number(out[i].cantidad) + Number(f.cantidad) };
      else out.push({ ...f, k: f.codigoBarras });
    });
    return out;
  });

  const buscarModelo = async (e) => {
    e.preventDefault();
    const q = codigo.trim();
    if (!q) return;
    try {
      const a = await posGet("/etiquetas/modelo", { area: tienda, q });
      setCodigo("");
      const conVariantes = a.variantes.length > 1 || (a.variantes[0] && (a.variantes[0].colores || a.variantes[0].tallas));
      // Escanearon una etiqueta: se agrega una más de esa misma talla y color
      const escaneada = a.variantes.find((v) => v.codigoBarras === q);
      if (escaneada) return agrega([{ codigo: a.codigo, nombre: a.nombre, precioTienda: a.precioTienda, color: escaneada.color, talla: escaneada.talla, codigoBarras: escaneada.codigoBarras, cantidad: 1 }]);
      if (conVariantes) return setPicker(a);
      const v = a.variantes[0];
      agrega([{ codigo: a.codigo, nombre: a.nombre, precioTienda: a.precioTienda, codigoBarras: v.codigoBarras, cantidad: Math.max(v.existencia, 1) }]);
    } catch (er) { avisaError(er); }
  };

  const deEntrada = async (en) => {
    try {
      const filas = await posGet(`/etiquetas/entrada/${en._id}`, { area: tienda });
      agrega(filas);
      Swal.fire({ icon: "success", title: `${numero(filas.reduce((t, f) => t + f.cantidad, 0))} etiquetas agregadas`, text: `Entrada ${en.folio}`, timer: 1500, showConfirmButton: false });
    } catch (er) { avisaError(er); }
  };

  const total = lista.reduce((t, f) => t + (parseInt(f.cantidad, 10) || 0), 0);
  const imprimir = () => {
    try { localStorage.setItem(FORMATO_KEY, formato); } catch (e) { /* sin storage */ }
    imprimirEtiquetas(lista, { formato, conPrecio });
  };

  if (!(user && user.rolTiendas && (user.posEtiquetas || user.posEntradas))) return <SinPermiso que="imprimir etiquetas" />;

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-barcode mr-2" style={{ color: "#0f172a" }} />Etiquetas</h3>
            <p className="page-subtitle">Código de barras por talla y color · al escanearlo en caja se agrega directo esa prenda</p>
          </div>
          <div className="page-actions">
            <TiendaSelector ctx={ctx} />
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-5 mb-3">
            <div className="form-section">
              <div className="form-section-header"><i className="fas fa-tshirt" /><h4 className="form-section-title">Por modelo</h4></div>
              <div className="form-section-body">
                <form onSubmit={buscarModelo} style={{ display: "flex", gap: 8 }}>
                  <Input value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="Código del modelo o de una etiqueta" />
                  <Button color="primary" type="submit">Agregar</Button>
                </form>
                <div className="small text-muted mt-2">Se abre la cuadrícula para indicar cuántas etiquetas de cada talla y color.</div>
              </div>
            </div>
            <div className="form-section">
              <div className="form-section-header"><i className="fas fa-dolly" /><h4 className="form-section-title">Por entrada (últimos 30 días)</h4></div>
              <div className="form-section-body" style={{ maxHeight: 280, overflowY: "auto" }}>
                {!entradas.length && <div className="text-muted small">Sin entradas recientes en esta tienda.</div>}
                {entradas.map((en) => (
                  <div key={en._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f1f5f9" }}>
                    <span style={{ fontSize: "0.85rem" }}><strong>Entrada {en.folio}</strong> · {fechaHora(en.fecha)} · {numero(en.piezas)} pzs<br /><span className="text-muted small">{en.observaciones}</span></span>
                    <Button size="sm" color="success" onClick={() => deEntrada(en)}>Una por pieza</Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-lg-7 mb-3">
            <div className="form-section">
              <div className="form-section-header dark"><i className="fas fa-tags" /><h4 className="form-section-title">Por imprimir</h4></div>
              <div className="form-section-body">
                <div className="filter-bar">
                  <div><label>Formato</label>
                    <Input type="select" value={formato} onChange={(e) => setFormato(e.target.value)}>
                      <option value="rollo">Impresora de etiquetas (50 × 25 mm)</option>
                      <option value="carta">Hoja carta, 30 por hoja</option>
                    </Input>
                  </div>
                  <div style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" id="conPrecio" checked={conPrecio} onChange={(e) => setConPrecio(e.target.checked)} />
                    <label htmlFor="conPrecio" className="mb-0" style={{ textTransform: "none" }}>Imprimir precio</label>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead><tr><th>Modelo</th><th>Color · Talla</th><th>Código</th><th style={{ width: 90 }}>Etiquetas</th><th /></tr></thead>
                    <tbody>
                      {!lista.length && <tr><td colSpan={5} className="text-center text-muted py-3">Agrega un modelo o una entrada</td></tr>}
                      {lista.map((f) => (
                        <tr key={f.k}>
                          <td><strong>{f.codigo}</strong> {f.nombre}</td>
                          <td>{variante(f) || "—"}</td>
                          <td style={{ fontFamily: "monospace" }}>{f.codigoBarras}</td>
                          <td><Input bsSize="sm" type="number" min="0" value={f.cantidad} onChange={(e) => setLista(lista.map((x) => (x.k === f.k ? { ...x, cantidad: e.target.value } : x)))} /></td>
                          <td><Button size="sm" color="danger" onClick={() => setLista(lista.filter((x) => x.k !== f.k))}><i className="fas fa-times" /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="submit-bar">
                  {lista.length > 0 && <Button color="secondary" onClick={() => setLista([])}>Limpiar</Button>}
                  <Button color="success" size="lg" disabled={!total} onClick={imprimir}><i className="fas fa-print mr-2" />Imprimir {numero(total)} etiqueta(s)</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <VariantePicker
        articulo={picker}
        modo="etiquetas"
        onAgregar={(filas) => {
          agrega(filas.map((v) => ({ codigo: picker.codigo, nombre: picker.nombre, precioTienda: picker.precioTienda, color: v.color, talla: v.talla, codigoBarras: v.codigoBarras, cantidad: v.cantidad })));
          setPicker(null);
        }}
        onCerrar={() => setPicker(null)}
      />
    </>
  );
}

export default PosEtiquetas;
